// RoomVisualizer.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { toAssetUrl } from "../services/api";
import { DEFAULT_REGION_SETTINGS } from "../data/regionSettings";
import RegionSelector from "./RegionSelector";

function getRegionSettings(regionEntry) {
  return regionEntry?.settings ?? DEFAULT_REGION_SETTINGS;
}

const DEFAULT_SHADOW_AO_STRENGTH = 0.28;

function loadImage(path, label) {
  const url = toAssetUrl(path);
  if (!url) {
    return Promise.reject(new Error(`Missing image path for ${label}`));
  }


  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${label}: ${url}`));
    img.src = url;
  });
}

function getRenderableRegions(data) {
  if (Array.isArray(data?.segments)) {
    return data.segments
      .filter((region) => region?.id && region?.mask_path)
      .map((region) => ({
        ...region,
        label: region.label || region.id,
        maskPath: region.mask_path,
      }));
  }


  if (Array.isArray(data?.region_masks)) {
    return data.region_masks
      .filter((region) => region?.id && (region?.mask_path || region?.mask))
      .map((region) => ({
        ...region,
        label: region.label || region.id,
        maskPath: region.mask_path || region.mask,
      }));
  }


  return Object.entries(data?.mask_paths || {}).map(([id, maskPath]) => ({
    id,
    label: id,
    maskPath,
  }));
}

function createConstantDepthData(width, height, value = 255) {
  const depthData = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < depthData.length; i += 4) {
    depthData[i] = value;
    depthData[i + 1] = value;
    depthData[i + 2] = value;
    depthData[i + 3] = 255;
  }
  return depthData;
}

let neutralLightingTexture = null;
function getNeutralLightingTexture() {
  if (!neutralLightingTexture) {
    neutralLightingTexture = new THREE.DataTexture(
      new Uint8Array([255, 255, 255, 255]),
      1,
      1,
      THREE.RGBAFormat,
    );
    neutralLightingTexture.needsUpdate = true;
  }
  return neutralLightingTexture;
}

function createRoomLightingContext(image, width, height) {
  const sourceCanvas = document.createElement("canvas");
  sourceCanvas.width = width;
  sourceCanvas.height = height;
  const sourceCtx = sourceCanvas.getContext("2d", { willReadFrequently: true });
  sourceCtx.drawImage(image, 0, 0, width, height);

  const source = sourceCtx.getImageData(0, 0, width, height);
  const grayscale = sourceCtx.createImageData(width, height);

  for (let i = 0; i < source.data.length; i += 4) {
    const r = source.data[i] / 255;
    const g = source.data[i + 1] / 255;
    const b = source.data[i + 2] / 255;
    const luma = Math.round((0.299 * r + 0.587 * g + 0.114 * b) * 255);
    grayscale.data[i] = luma;
    grayscale.data[i + 1] = luma;
    grayscale.data[i + 2] = luma;
    grayscale.data[i + 3] = 255;
  }

  sourceCtx.putImageData(grayscale, 0, 0);

  // Downsample then upsample to remove old wallpaper/texture detail while
  // keeping broad illumination gradients, shadows, and highlights.
  const blurScale = 16;
  const smallCanvas = document.createElement("canvas");
  smallCanvas.width = Math.max(16, Math.round(width / blurScale));
  smallCanvas.height = Math.max(16, Math.round(height / blurScale));
  const smallCtx = smallCanvas.getContext("2d");
  smallCtx.imageSmoothingEnabled = true;
  smallCtx.imageSmoothingQuality = "high";
  smallCtx.drawImage(sourceCanvas, 0, 0, smallCanvas.width, smallCanvas.height);

  const lightingCanvas = document.createElement("canvas");
  lightingCanvas.width = width;
  lightingCanvas.height = height;
  const lightingCtx = lightingCanvas.getContext("2d", { willReadFrequently: true });
  lightingCtx.imageSmoothingEnabled = true;
  lightingCtx.imageSmoothingQuality = "high";
  lightingCtx.drawImage(smallCanvas, 0, 0, width, height);

  const lightingImage = lightingCtx.getImageData(0, 0, width, height);
  const luma = new Float32Array(width * height);
  for (let p = 0, i = 0; p < luma.length; p++, i += 4) {
    const value = lightingImage.data[i];
    luma[p] = value / 255;
    lightingImage.data[i] = value;
    lightingImage.data[i + 1] = value;
    lightingImage.data[i + 2] = value;
    lightingImage.data[i + 3] = 255;
  }

  const texture = new THREE.DataTexture(
    lightingImage.data,
    width,
    height,
    THREE.RGBAFormat,
  );
  texture.flipY = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;

  let sumX = 0;
  let sumY = 0;
  let sumW = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const p = y * width + x;
      const weight = Math.max(0, luma[p] - 0.6);
      sumX += x * weight;
      sumY += y * weight;
      sumW += weight;
    }
  }
  const lightHint = sumW > 0
    ? { x: sumX / sumW / width, y: sumY / sumW / height }
    : { x: 0.5, y: 0.2 };

  return { texture, luma, width, height, lightHint };
}

function computeMaskedMeanLuma(maskData, lumaData, width, height) {
  if (!maskData || !lumaData) return 1.0;

  let sum = 0;
  let count = 0;
  const stride = Math.max(1, Math.floor((width * height) / 60000));
  for (let p = 0; p < width * height; p += stride) {
    if (maskData[p * 4] <= 127) continue;
    sum += lumaData[p];
    count++;
  }

  return count > 0 ? Math.max(0.05, sum / count) : 1.0;
}

function updateDirectionalLightFromLightingContext(keyLight, lightingContext) {
  if (!keyLight || !lightingContext?.lightHint) return;

  const { x, y } = lightingContext.lightHint;
  const lightX = (x - 0.5) * 7;
  const lightY = Math.max(1.8, (0.55 - y) * 7 + 1.2);
  const lightZ = 4.5;

  keyLight.position.set(lightX, lightY, lightZ);
  keyLight.target.position.set(0, 0, -6);
  keyLight.target.updateMatrixWorld();
}

function applyTextureQuality(texture, renderer) {
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  if (renderer?.capabilities?.getMaxAnisotropy) {
    texture.anisotropy = Math.min(16, renderer.capabilities.getMaxAnisotropy());
  }
  texture.needsUpdate = true;
}

function addTileShaderEnhancements(
  material,
  lightingContext,
  regionName,
  settings,
) {
  const groutWidth = (settings.groutWidth || 0) / Math.max(settings.tileWidth || 1, 0.0001);
  const groutColor = new THREE.Color(settings.groutColor || "#d9d9d9");
  const surfaceMeanLuma = lightingContext?.meanLumaByRegion?.[regionName] ?? 1.0;
  const aoStrength = settings.shadowAoStrength ?? settings.shadowStrength ?? DEFAULT_SHADOW_AO_STRENGTH;

  material.extensions = { derivatives: true };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.lightingTexture = {
      value: lightingContext?.texture || getNeutralLightingTexture(),
    };
    shader.uniforms.surfaceMeanLuma = { value: surfaceMeanLuma };
    shader.uniforms.shadowAoStrength = { value: aoStrength };
    shader.uniforms.groutWidth = { value: groutWidth };
    shader.uniforms.groutColor = { value: groutColor };

    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `
        #include <common>
        attribute vec2 imageUv;
        varying vec2 vImageUv;
        `,
      )
      .replace(
        "#include <uv_vertex>",
        `
        #include <uv_vertex>
        vImageUv = imageUv;
        `,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `
        #include <common>
        uniform sampler2D lightingTexture;
        uniform float surfaceMeanLuma;
        uniform float shadowAoStrength;
        uniform float groutWidth;
        uniform vec3 groutColor;
        varying vec2 vImageUv;
        `,
      )
      .replace(
        "#include <map_fragment>",
        `
        #ifdef USE_MAP
          vec4 sampledDiffuseColor = texture2D(map, vMapUv);
          diffuseColor *= sampledDiffuseColor;

          vec2 localCoord = fract(vMapUv);
          vec2 edgeDist = min(localCoord, 1.0 - localCoord);
          vec2 filterWidth = max(abs(dFdx(vMapUv)), abs(dFdy(vMapUv)));
          vec2 groutStep = smoothstep(vec2(groutWidth) + filterWidth, vec2(groutWidth), edgeDist);
          float groutFactor = max(groutStep.x, groutStep.y);
          diffuseColor.rgb = mix(diffuseColor.rgb, groutColor, groutFactor);
        #endif
        `,
      )
      .replace(
        "#include <opaque_fragment>",
        `
        float roomLuma = texture2D(lightingTexture, vImageUv).r;
        float relativeAo = clamp(roomLuma / max(surfaceMeanLuma, 0.05), 0.55, 1.35);
        float photoAo = clamp(mix(1.0, relativeAo, shadowAoStrength), 0.72, 1.16);
        outgoingLight *= photoAo;
        #include <opaque_fragment>
        `,
      );
  };

  material.customProgramCacheKey = () =>
    `tile-standard-${regionName}-${groutColor.getHexString()}-${groutWidth}-${aoStrength}`;
}

function stabilizeGeometryNormals(geometry, strength = 0.88) {
  geometry.computeVertexNormals();
  const normalAttr = geometry.getAttribute("normal");
  if (!normalAttr || normalAttr.count === 0) return;

  const average = new THREE.Vector3();
  const current = new THREE.Vector3();
  for (let i = 0; i < normalAttr.count; i++) {
    current.fromBufferAttribute(normalAttr, i);
    average.add(current);
  }
  average.normalize();
  if (average.z < 0) average.negate();

  for (let i = 0; i < normalAttr.count; i++) {
    current.fromBufferAttribute(normalAttr, i);
    current.lerp(average, strength).normalize();
    normalAttr.setXYZ(i, current.x, current.y, current.z);
  }
  normalAttr.needsUpdate = true;
}

function getLineSide(x, y, line) {
  return (
    (x - line.start.x) * (line.end.y - line.start.y) -
    (y - line.start.y) * (line.end.x - line.start.x)
  );
}

function splitMaskByLine(maskData, width, height, line, keepPositiveSide) {
  const split = new Uint8ClampedArray(maskData.length);


  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (maskData[i] <= 127) continue;


      const isPositive = getLineSide(x, y, line) >= 0;
      if (isPositive !== keepPositiveSide) continue;


      split[i] = 255;
      split[i + 1] = 255;
      split[i + 2] = 255;
      split[i + 3] = 255;
    }
  }


  return split;
}

function getMaskBBox(maskData, width, height, fallback) {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;


  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (maskData[i] <= 127) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }


  if (maxX < minX || maxY < minY) return fallback;


  return {
    x: minX,
    y: minY,
    w: Math.max(1, maxX - minX),
    h: Math.max(1, maxY - minY),
  };
}

function createSplitChildRegions(parentRegion, op) {
  const bbox = parentRegion.bbox || { x: 0, y: 0, w: 1, h: 1 };
  const lineMidX = Math.max(
    bbox.x + 1,
    Math.min(bbox.x + bbox.w - 1, (op.line.start.x + op.line.end.x) / 2),
  );
  const firstBBox = {
    x: bbox.x,
    y: bbox.y,
    w: Math.max(1, lineMidX - bbox.x),
    h: bbox.h,
  };
  const secondBBox = {
    x: lineMidX,
    y: bbox.y,
    w: Math.max(1, bbox.x + bbox.w - lineMidX),
    h: bbox.h,
  };
  const base = {
    label: parentRegion.label,
    sourceId: parentRegion.sourceId || parentRegion.id,
    manualSplit: true,
    wall_position: parentRegion.wall_position,
    maskPath: parentRegion.maskPath,
  };


  return [
    {
      ...parentRegion,
      ...base,
      id: op.childAId,
      splitSide: true,
      splitLine: op.line,
      bbox: firstBBox,
    },
    {
      ...parentRegion,
      ...base,
      id: op.childBId,
      splitSide: false,
      splitLine: op.line,
      bbox: secondBBox,
    },
  ];
}

function createSplitOp(parentRegion, line) {
  return {
    parentId: parentRegion.id,
    line,
    childAId: `${parentRegion.id}_split_1`,
    childBId: `${parentRegion.id}_split_2`,
  };
}

function buildRenderableRegions(baseRegions, splitOps) {
  const splitByParent = new Map(splitOps.map((op) => [op.parentId, op]));

  function expand(region) {
    const op = splitByParent.get(region.id);
    if (!op) return [region];

    const [childA, childB] = createSplitChildRegions(region, op);
    return [...expand(childA), ...expand(childB)];
  }

  return baseRegions.flatMap(expand);
}

function applySplitMasks(masks, splitOps, width, height, regionById) {
  splitOps.forEach((op) => {
    const parentMask = masks[op.parentId];
    if (!parentMask) return;


    const maskA = splitMaskByLine(parentMask, width, height, op.line, true);
    const maskB = splitMaskByLine(parentMask, width, height, op.line, false);
    masks[op.childAId] = maskA;
    masks[op.childBId] = maskB;


    const childA = regionById.get(op.childAId);
    const childB = regionById.get(op.childBId);
    if (childA) {
      childA.bbox = getMaskBBox(maskA, width, height, childA.bbox);
    }
    if (childB) {
      childB.bbox = getMaskBBox(maskB, width, height, childB.bbox);
    }
  });
}

function buildRegionGeometry(
  regionName,
  masks,
  depthData,
  intrinsics,
  roomDepthFt,
  isFloor,
  isCeiling,
  tileWidth,
  tileHeight,
  cameraHeight = 1.5,
  floorLayout = null,
  wallLayout = null
) {
  const { width, height, fx, fy, cx, cy } = intrinsics;
  const vertices = [];
  const uvs = [];
  const imageUvs = [];
  const indices = [];
  const gridIndices = Array.from({ length: height }, () =>
    new Int32Array(width).fill(-1),
  );
  let vertexCount = 0;
  const maskData = masks[regionName];
  if (!maskData) return null;


  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (maskData[i] <= 127) continue;


      const d = Math.max(depthData[i] / 255, 0.05);
      const Z = -d * roomDepthFt;
      const X = ((x - cx) / fx) * Math.abs(Z);
      const Y = -((y - cy) / fy) * Math.abs(Z);


      vertices.push(X, Y, Z);
      imageUvs.push(
        width > 1 ? x / (width - 1) : 0,
        height > 1 ? 1 - y / (height - 1) : 1,
      );
      let uvX, uvY;


      if (isFloor || isCeiling) {
        const rayDirY = -((y - cy) / fy);
        if (Math.abs(rayDirY) > 0.0001) {
          const t = cameraHeight / Math.abs(rayDirY);
          const floorX = ((x - cx) / fx) * t;
          const floorZ = -t;

          if (floorLayout?.origin && floorLayout?.axis_x && floorLayout?.axis_y) {
            const relX = floorX - floorLayout.origin.x;
            const relZ = floorZ - floorLayout.origin.z;
            const localX = relX * floorLayout.axis_x.x + relZ * floorLayout.axis_x.z;
            const localY = relX * floorLayout.axis_y.x + relZ * floorLayout.axis_y.z;
            uvX = localX / tileWidth;
            uvY = localY / tileHeight;
          } else {
            uvX = floorX / (tileWidth * 0.7);
            uvY = floorZ / (tileHeight * 0.7);
          }
        } else {
          uvX = X / tileWidth;
          uvY = Z / tileHeight;
        }
      } else if (wallLayout?.axis_u && wallLayout?.axis_v) {
        const [ux, uy, uz] = wallLayout.axis_u;
        const [vx, vy, vz] = wallLayout.axis_v;
        let resolved = false;

        // Intersect this pixel's camera ray with the backend-fitted plane n·P = plane_d.
        if (Number.isFinite(wallLayout.plane_d) && Array.isArray(wallLayout.normal)) {
          const [nx, ny, nz] = wallLayout.normal;
          const rx = (x - cx) / fx;
          const ry = -((y - cy) / fy);
          const denom = nx * rx + ny * ry - nz;       // n · (rx, ry, -1)
          if (Math.abs(denom) > 1e-4) {
            const t = wallLayout.plane_d / denom;     // distance along the ray
            if (t > 0.05 && t < 1e4) {
              uvX = (rx * t * ux + ry * t * uy - t * uz) / tileWidth;
              uvY = (rx * t * vx + ry * t * vy - t * vz) / tileHeight;
              resolved = true;
              // Optional: align horizontal rows across ALL walls to the floor:
              // uvY = (ry * t + cameraHeight) / tileHeight;
            }
          }
        }
        if (!resolved) {  // fallback: project the depth-based vertex
          uvX = (X * ux + Y * uy + Z * uz) / tileWidth;
          uvY = (X * vx + Y * vy + Z * vz) / tileHeight;
        }
      } else {
        uvX = X / tileWidth;
        uvY = Y / tileHeight;
      }


      uvs.push(uvX, uvY);
      gridIndices[y][x] = vertexCount++;
    }
  }

  for (let y = 0; y < height - 1; y++) {
    for (let x = 0; x < width - 1; x++) {
      const i0 = gridIndices[y][x];
      const i1 = gridIndices[y][x + 1];
      const i2 = gridIndices[y + 1][x];
      const i3 = gridIndices[y + 1][x + 1];
      if (i0 !== -1 && i1 !== -1 && i2 !== -1) indices.push(i0, i2, i1);
      if (i1 !== -1 && i2 !== -1 && i3 !== -1) indices.push(i1, i2, i3);
    }
  }


  if (vertices.length === 0) return null;


  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geo.setAttribute("imageUv", new THREE.Float32BufferAttribute(imageUvs, 2));
  geo.setIndex(indices);
  stabilizeGeometryNormals(geo);


  geo.computeBoundingBox();


  return geo;
}

async function applyMaterialsToMeshes(
  meshMap,
  regionMaterials,
  lightingContext = null,
  renderer = null,
) {
  const safeRegionMaterials = regionMaterials || {};

  await Promise.all(
    Object.entries(meshMap).map(async ([regionName, mesh]) => {
      const regionEntry = safeRegionMaterials[regionName];


      if (!regionEntry) {
        mesh.visible = false;
        return;
      }


      mesh.visible = true;


      const newMat = await buildMaterial(
        regionName,
        regionEntry,
        lightingContext,
        renderer,
      );


      mesh.material.dispose();
      mesh.material = newMat;
    }),
  );
}

async function buildMaterial(regionName, regionEntry, lightingContext = null, renderer = null) {
  const settings = getRegionSettings(regionEntry);
  const materialConfig = regionEntry;

  if (!materialConfig) {
    return new THREE.MeshBasicMaterial({
      color: 0xcccccc,
      side: THREE.DoubleSide,
    });
  }

  if (materialConfig.type === "color" || materialConfig.value?.startsWith("#")) {
    const material = new THREE.MeshBasicMaterial({
      color: materialConfig.value,
      side: THREE.DoubleSide,
    });
    addTileShaderEnhancements(material, lightingContext, regionName, settings);
    return material;
  }

  try {
    const img = await loadImage(materialConfig.value, `${regionName} texture`);
    const texture = new THREE.Texture(img);
    applyTextureQuality(texture, renderer);

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      color: 0xffffff,
      side: THREE.DoubleSide,
    });
    addTileShaderEnhancements(material, lightingContext, regionName, settings);
    return material;
  } catch (err) {
    console.error(`Texture load failed for ${regionName}:`, err);
    return new THREE.MeshBasicMaterial({
      color: 0xcccccc,
      side: THREE.DoubleSide,
    });
  }
}

export default function RoomVisualizer({
  data,
  selectedRegions: selectedRegionsProp,
  setSelectedRegions: setSelectedRegionsProp,
  regionMaterials: regionMaterialsProp,
} = {}) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const selectedRegions = selectedRegionsProp || {};
  const setSelectedRegions = setSelectedRegionsProp || (() => {});
  const regionMaterials = regionMaterialsProp || {};


  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animFrameRef = useRef(null);
  const meshMapRef = useRef({});
  const keyLightRef = useRef(null);
  const lightingContextRef = useRef(null);
  const imageAspectRef = useRef(null);
  const regionMaterialsRef = useRef(regionMaterials);
  regionMaterialsRef.current = regionMaterials;


  const regionTileDimensionsKey = useMemo(
    () =>
      JSON.stringify(
        Object.entries(regionMaterials).map(([id, entry]) => [
          id,
          entry?.settings?.tileWidth,
          entry?.settings?.tileHeight,
        ]),
      ),
    [regionMaterials],
  );

  const [splitOps, setSplitOps] = useState([]);
  const [splitMode, setSplitMode] = useState(false);
  const [draftLine, setDraftLine] = useState(null);
  const dragStartRef = useRef(null);


  const baseRegions = useMemo(() => getRenderableRegions(data), [data]);
  const renderableRegions = useMemo(
    () => buildRenderableRegions(baseRegions, splitOps),
    [baseRegions, splitOps],
  );
  const splitParentIds = useMemo(
    () => new Set(splitOps.map((op) => op.parentId)),
    [splitOps],
  );
  const selectedRegionId = useMemo(
    () => renderableRegions.find((region) => selectedRegions[region.id])?.id,
    [renderableRegions, selectedRegions],
  );


  const selectedRegion = useMemo(
    () => renderableRegions.find((region) => region.id === selectedRegionId),
    [renderableRegions, selectedRegionId],
  );


  const canSplitSelectedRegion =
    selectedRegion &&
    selectedRegion.label?.toLowerCase().includes("wall") &&
    !splitParentIds.has(selectedRegion.id);


  const toImagePoint = (event) => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !data?.camera_intrinsics) return null;


    const rect = wrapper.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * data.camera_intrinsics.width;
    const y = ((event.clientY - rect.top) / rect.height) * data.camera_intrinsics.height;


    return {
      x: Math.max(0, Math.min(data.camera_intrinsics.width - 1, x)),
      y: Math.max(0, Math.min(data.camera_intrinsics.height - 1, y)),
    };
  };


  const toViewPoint = (point) => {
    if (!data?.camera_intrinsics) return { x: 0, y: 0 };
    return {
      x: (point.x / data.camera_intrinsics.width) * 100,
      y: (point.y / data.camera_intrinsics.height) * 100,
    };
  };


  const handleSplitPointerDown = (event) => {
    if (!splitMode || !canSplitSelectedRegion) return;
    const point = toImagePoint(event);
    if (!point) return;
    dragStartRef.current = point;
    setDraftLine({ start: point, end: point });
  };


  const handleSplitPointerMove = (event) => {
    if (!splitMode || !dragStartRef.current) return;
    const point = toImagePoint(event);
    if (!point) return;
    setDraftLine({ start: dragStartRef.current, end: point });
  };


  const handleSplitPointerUp = (event) => {
    if (!splitMode || !dragStartRef.current || !selectedRegion) return;
    const point = toImagePoint(event);
    const start = dragStartRef.current;
    dragStartRef.current = null;


    if (!point) {
      setDraftLine(null);
      return;
    }


    const distance = Math.hypot(point.x - start.x, point.y - start.y);
    if (distance < 12) {
      setDraftLine(null);
      return;
    }


    const line = { start, end: point };
    const splitOp = createSplitOp(selectedRegion, line);
    const [childA, childB] = createSplitChildRegions(selectedRegion, splitOp);


    setSplitOps((prev) => [...prev, splitOp]);
    setSelectedRegions((prev) => {
      const next = { ...prev, [selectedRegion.id]: false };
      next[childA.id] = false;
      next[childB.id] = false;
      return next;
    });
    setDraftLine(null);
    setSplitMode(false);
  };


  const undoLastSplit = () => {
    const lastOp = splitOps[splitOps.length - 1];
    if (!lastOp) return;


    setSplitOps((prev) => prev.slice(0, -1));
    setSelectedRegions((selected) => {
      const next = { ...selected };
      delete next[lastOp.childAId];
      delete next[lastOp.childBId];
      return next;
    });
  };


  // EFFECT 1 — init once
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const container = canvasRef.current;
    if (!wrapper || !container) return;


    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;


    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(wrapper.clientWidth, wrapper.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;


    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.38);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xfff4e0, 1.35);
    keyLight.position.set(0, 4, 4.5);
    keyLight.target.position.set(0, 0, -6);
    scene.add(keyLight);
    scene.add(keyLight.target);
    keyLightRef.current = keyLight;


    // Camera — will be reconfigured when data arrives
    const camera = new THREE.PerspectiveCamera(
      60,
      wrapper.clientWidth / wrapper.clientHeight,
      0.1,
      1000,
    );
    camera.position.set(0, 0, 0);
    cameraRef.current = camera;


    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controlsRef.current = controls;


    // Render loop
    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();


    // Resize handler — re-fit canvas to parent area using stored image aspect
    const onResize = () => {
      if (!wrapper || !renderer || !camera) return;

      const imageAspect = imageAspectRef.current;
      if (imageAspect) {
        const parent = wrapper.parentElement;
        const maxW = Math.max((parent?.clientWidth || window.innerWidth) * 0.95, 120);
        const maxH = Math.max((parent?.clientHeight || window.innerHeight) * 0.95, 120);
        let w = maxW;
        let h = maxW / imageAspect;
        if (h > maxH) {
          h = maxH;
          w = maxH * imageAspect;
        }
        wrapper.style.width = `${w}px`;
        wrapper.style.height = `${h}px`;
        renderer.setSize(w, h);
        camera.aspect = imageAspect;
      } else {
        renderer.setSize(wrapper.clientWidth, wrapper.clientHeight);
        camera.aspect = wrapper.clientWidth / Math.max(wrapper.clientHeight, 1);
      }
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);


    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", onResize);
      controls.dispose();
      renderer.dispose();
      container.innerHTML = "";
      if (lightingContextRef.current?.texture) {
        lightingContextRef.current.texture.dispose();
      }
      lightingContextRef.current = null;
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      controlsRef.current = null;
      keyLightRef.current = null;
      meshMapRef.current = {};
    };
  }, []);


  // EFFECT 2 — rebuild geometry when data changes
  useEffect(() => {
    if (!data || !sceneRef.current) return;
    console.log("data: ",data);


    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    const wrapper = wrapperRef.current;


    const { width, height, fy } = data.camera_intrinsics;
    const imageAspect = width / height;
    imageAspectRef.current = imageAspect;


    // Resize wrapper to fit the available parent area (not full window),
    // so a large image cannot squeeze the sidebar.
    const parent = wrapper.parentElement;
    const maxW = Math.max((parent?.clientWidth || window.innerWidth) * 0.95, 120);
    const maxH = Math.max((parent?.clientHeight || window.innerHeight) * 0.95, 120);
    let w = maxW;
    let h = maxW / imageAspect;
    if (h > maxH) {
      h = maxH;
      w = maxH * imageAspect;
    }
    wrapper.style.width = `${w}px`;
    wrapper.style.height = `${h}px`;
    renderer.setSize(w, h);


    // Reconfigure camera from intrinsics
    const vFovDegrees = 2 * Math.atan(height / (2 * fy)) * (180 / Math.PI);
    camera.fov = vFovDegrees;
    camera.aspect = imageAspect;
    camera.updateProjectionMatrix();


    // Background image
    const backgroundUrl = toAssetUrl(data.image_path || data.room_image);
    wrapper.style.backgroundImage = backgroundUrl ? `url("${backgroundUrl}")` : "";


    let cancelled = false;


    const run = async () => {
      if (lightingContextRef.current?.texture) {
        lightingContextRef.current.texture.dispose();
      }
      lightingContextRef.current = null;

      let lightingContext = null;
      const roomImagePath = data.image_path || data.room_image;
      if (roomImagePath) {
        try {
          const roomImg = await loadImage(roomImagePath, "room lighting image");
          if (cancelled) return;
          lightingContext = createRoomLightingContext(roomImg, width, height);
        } catch (err) {
          console.warn("Room lighting map unavailable; rendering tiles without image lighting.", err);
        }
      }

      let depthData;
      if (data.depth_map_url) {
        const depthImg = await loadImage(data.depth_map_url, "depth map");
        const depthCanvas = document.createElement("canvas");
        depthCanvas.width = width;
        depthCanvas.height = height;
        const depthCtx = depthCanvas.getContext("2d");
        depthCtx.drawImage(depthImg, 0, 0, width, height);
        depthData = depthCtx.getImageData(0, 0, width, height).data;
      } else {
        console.warn("depth_map_url missing — mesh will be flat. Fix the backend response.");
        depthData = createConstantDepthData(width, height);
      }


      // ── Load combined mask and extract individual region masks ──
      const masks = {};
      
      if (data.combined_mask_path && data.label_mapping) {
        // Load single combined mask image
        const combinedMaskImg = await loadImage(data.combined_mask_path, "combined mask");
        const combinedCanvas = document.createElement("canvas");
        combinedCanvas.width = width;
        combinedCanvas.height = height;
        const combinedCtx = combinedCanvas.getContext("2d");
        combinedCtx.drawImage(combinedMaskImg, 0, 0, width, height);
        const combinedData = combinedCtx.getImageData(0, 0, width, height).data;
        
        // Extract individual region masks from combined mask using label mapping
        baseRegions.forEach((region) => {
          const labelValue = data.label_mapping[region.id];
          if (labelValue !== undefined) {
            const regionMaskData = new Uint8ClampedArray(width * height * 4);
            for (let i = 0; i < width * height; i++) {
              const pixelIndex = i * 4;
              // Check if pixel matches the region's label value
              if (combinedData[pixelIndex] === labelValue) {
                regionMaskData[pixelIndex] = 255;
                regionMaskData[pixelIndex + 1] = 255;
                regionMaskData[pixelIndex + 2] = 255;
                regionMaskData[pixelIndex + 3] = 255;
              } else {
                regionMaskData[pixelIndex] = 0;
                regionMaskData[pixelIndex + 1] = 0;
                regionMaskData[pixelIndex + 2] = 0;
                regionMaskData[pixelIndex + 3] = 255;
              }
            }
            masks[region.id] = regionMaskData;
          }
        });
      } else {
        // Fallback to loading individual masks (backward compatibility)
        await Promise.all(
          baseRegions.map(async (region) => {
            const maskImg = await loadImage(region.maskPath, `${region.id} mask`);
            const mc = document.createElement("canvas");
            mc.width = width;
            mc.height = height;
            const mctx = mc.getContext("2d");
            mctx.drawImage(maskImg, 0, 0, width, height);
            masks[region.id] = mctx.getImageData(0, 0, width, height).data;
          }),
        );
      }


      const regionById = new Map(renderableRegions.map((region) => [region.id, region]));
      applySplitMasks(masks, splitOps, width, height, regionById);

      if (lightingContext) {
        const meanLumaByRegion = {};
        Object.entries(masks).forEach(([regionId, maskData]) => {
          meanLumaByRegion[regionId] = computeMaskedMeanLuma(
            maskData,
            lightingContext.luma,
            width,
            height,
          );
        });
        lightingContext.meanLumaByRegion = meanLumaByRegion;
        lightingContextRef.current = lightingContext;
        updateDirectionalLightFromLightingContext(keyLightRef.current, lightingContext);
      }


      if (cancelled) return;


      // ── Remove old meshes from scene ──
      Object.values(meshMapRef.current).forEach((mesh) => {
        scene.remove(mesh);
        mesh.geometry.dispose();
        if (mesh.material?.dispose) mesh.material.dispose();
      });
      meshMapRef.current = {};


      // ── Build geometry + initial materials for each region ──
      await Promise.all(
        renderableRegions.map(async (region) => {
          if (cancelled) return;
          const regionName = region.id;
          const isFloor = region.label?.toLowerCase().includes("floor");
          const isCeiling = region.label?.toLowerCase().includes("ceiling");
          const regionSettings = getRegionSettings(
            regionMaterialsRef.current[regionName],
          );
          const roomDepthFt = data.depth_scale ?? 10.0;
          const geo = buildRegionGeometry(
            regionName, masks, depthData, data.camera_intrinsics,
            roomDepthFt,                       // was hardcoded 10.0
            isFloor, isCeiling, regionSettings.tileWidth, regionSettings.tileHeight,
            data.camera_height ?? 2,           // was hardcoded 2
            data.floor_layout, region.wall_layout,
          );
          if (!geo) return;
          if (cancelled) {
            geo.dispose();
            return;
          }


          const mesh = new THREE.Mesh(
            geo,
            new THREE.MeshBasicMaterial({
              transparent: true,
              opacity: 0,
            }),
          );


          mesh.visible = false;
          meshMapRef.current[regionName] = mesh;
          scene.add(mesh);
        }),
      );


      if (cancelled) return;


      await applyMaterialsToMeshes(
        meshMapRef.current,
        regionMaterialsRef.current,
        lightingContextRef.current,
        renderer,
      );
    };


    run().catch((err) => console.error("Geometry build failed:", err));


    return () => {
      cancelled = true;
    };
  }, [
    data,
    baseRegions,
    renderableRegions,
    splitOps,
    regionTileDimensionsKey,
  ]);


  // EFFECT 3 — swap materials only (no geometry rebuild)
  useEffect(() => {
    if (!sceneRef.current || Object.keys(meshMapRef.current).length === 0)
      return;


    let cancelled = false;


    const run = async () => {
      if (cancelled) return;
      await applyMaterialsToMeshes(
        meshMapRef.current,
        regionMaterials,
        lightingContextRef.current,
        rendererRef.current,
      );
    };


    run().catch((err) => console.error("Material update failed:", err));


    return () => {
      cancelled = true;
    };
  }, [regionMaterials]);


  return (
    <div
      ref={wrapperRef}
      className="relative shadow-2xl bg-contain bg-no-repeat bg-center"
    >
      <div ref={canvasRef} className="absolute inset-0" />
      {/* <div className="absolute right-4 top-4 z-40 flex items-center gap-2">
        <button
          type="button"
          disabled={!canSplitSelectedRegion}
          onClick={() => setSplitMode((value) => !value)}
          className={`px-3 py-2 text-sm rounded-lg border ${
            splitMode
              ? "bg-blue-600 text-white border-blue-400"
              : "bg-slate-950/80 text-white border-white/20"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          Split wall
        </button>
        <button
          type="button"
          disabled={splitOps.length === 0}
          onClick={undoLastSplit}
          className="px-3 py-2 text-sm rounded-lg border bg-slate-950/80 text-white border-white/20 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Undo
        </button>
      </div> */}
      {(splitMode || splitOps.length > 0) && (
        <svg
          className={`absolute inset-0 z-30 h-full w-full ${
            splitMode ? "cursor-crosshair pointer-events-auto" : "pointer-events-none"
          }`}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          onPointerDown={handleSplitPointerDown}
          onPointerMove={handleSplitPointerMove}
          onPointerUp={handleSplitPointerUp}
          onPointerCancel={() => {
            dragStartRef.current = null;
            setDraftLine(null);
          }}
        >
          {splitOps.map((op, index) => {
            const start = toViewPoint(op.line.start);
            const end = toViewPoint(op.line.end);
            return (
              <line
                key={`${op.parentId}-${index}`}
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                stroke="#38bdf8"
                strokeWidth="0.45"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {draftLine && (
            <line
              x1={toViewPoint(draftLine.start).x}
              y1={toViewPoint(draftLine.start).y}
              x2={toViewPoint(draftLine.end).x}
              y2={toViewPoint(draftLine.end).y}
              stroke="#f97316"
              strokeWidth="0.6"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      )}
      <RegionSelector
        regions={renderableRegions}
        selectedRegions={selectedRegions}
        setSelectedRegions={setSelectedRegions}
        imageWidth={data?.camera_intrinsics?.width}
        imageHeight={data?.camera_intrinsics?.height}
      />
    </div>
  );
}
